/**
 * Application routes.
 *
 * Public pages are lazily loaded per route group and the admin bundle is a separate chunk, so a
 * visitor reading a blog post never downloads the CMS. Route paths match the sitemap in
 * `docs/02-sitemap-and-ia.md`, and each one is independently indexable because Django renders the
 * document `<head>` server-side.
 */
import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { SiteLayout } from '@/layouts/SiteLayout';
import { PageSkeleton } from '@/components/site/blocks';

const HomePage = lazy(() => import('@/pages/public/HomePage'));

const SolutionIndexPage = lazy(() =>
  import('@/pages/public/SolutionPages').then((m) => ({ default: m.SolutionIndexPage })),
);
const SolutionDetailPage = lazy(() =>
  import('@/pages/public/SolutionPages').then((m) => ({ default: m.SolutionDetailPage })),
);
const IndustrySolutionsPage = lazy(() =>
  import('@/pages/public/SolutionPages').then((m) => ({ default: m.IndustrySolutionsPage })),
);

const ProductIndexPage = lazy(() => import('@/pages/public/ProductPages').then((m) => ({ default: m.ProductIndexPage })));
const ProductDetailPage = lazy(() => import('@/pages/public/ProductPages').then((m) => ({ default: m.ProductDetailPage })));

const ServiceIndexPage = lazy(() =>
  import('@/pages/public/ServiceIndustryPages').then((m) => ({ default: m.ServiceIndexPage })),
);
const ServiceDetailPage = lazy(() =>
  import('@/pages/public/ServiceIndustryPages').then((m) => ({ default: m.ServiceDetailPage })),
);
const IndustryIndexPage = lazy(() =>
  import('@/pages/public/ServiceIndustryPages').then((m) => ({ default: m.IndustryIndexPage })),
);
const IndustryDetailPage = lazy(() =>
  import('@/pages/public/ServiceIndustryPages').then((m) => ({ default: m.IndustryDetailPage })),
);
const TechnologyPage = lazy(() =>
  import('@/pages/public/ServiceIndustryPages').then((m) => ({ default: m.TechnologyPage })),
);

const CaseStudyIndexPage = lazy(() =>
  import('@/pages/public/CaseStudyPages').then((m) => ({ default: m.CaseStudyIndexPage })),
);
const CaseStudyDetailPage = lazy(() =>
  import('@/pages/public/CaseStudyPages').then((m) => ({ default: m.CaseStudyDetailPage })),
);

const ResourceIndexPage = lazy(() =>
  import('@/pages/public/ResourcePages').then((m) => ({ default: m.ResourceIndexPage })),
);
const ArticleDetailPage = lazy(() =>
  import('@/pages/public/ResourcePages').then((m) => ({ default: m.ArticleDetailPage })),
);
const DownloadsPage = lazy(() => import('@/pages/public/ResourcePages').then((m) => ({ default: m.DownloadsPage })));
const SitemapPage = lazy(() => import('@/pages/public/ResourcePages').then((m) => ({ default: m.SitemapPage })));

const CareersIndexPage = lazy(() => import('@/pages/public/CareersPages').then((m) => ({ default: m.CareersIndexPage })));
const JobDetailPage = lazy(() => import('@/pages/public/CareersPages').then((m) => ({ default: m.JobDetailPage })));

const ContactPage = lazy(() => import('@/pages/public/ContactPage').then((m) => ({ default: m.ContactPage })));

const AboutPage = lazy(() => import('@/pages/public/AboutPages').then((m) => ({ default: m.AboutPage })));
const LeadershipPage = lazy(() => import('@/pages/public/AboutPages').then((m) => ({ default: m.LeadershipPage })));
const VisionMissionPage = lazy(() => import('@/pages/public/AboutPages').then((m) => ({ default: m.VisionMissionPage })));
const CertificationsPage = lazy(() => import('@/pages/public/AboutPages').then((m) => ({ default: m.CertificationsPage })));
const MilestonesPage = lazy(() => import('@/pages/public/AboutPages').then((m) => ({ default: m.MilestonesPage })));
const CmsPage = lazy(() => import('@/pages/public/AboutPages').then((m) => ({ default: m.CmsPage })));

const NotFoundPage = lazy(() => import('@/pages/public/NotFoundPage'));

const AdminApp = lazy(() => import('@/pages/admin/AdminApp'));

function Loading() {
  return <PageSkeleton />;
}

export function App() {
  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        {/* CMS — a separate bundle, never downloaded by public visitors. */}
        <Route path="/admin/*" element={<AdminApp />} />

        <Route element={<SiteLayout />}>
          <Route index element={<HomePage />} />

          <Route path="solutions">
            <Route index element={<SolutionIndexPage />} />
            <Route path="industry/:industry" element={<IndustrySolutionsPage />} />
            <Route path=":slug" element={<SolutionDetailPage />} />
          </Route>

          <Route path="products">
            <Route index element={<ProductIndexPage />} />
            <Route path=":slug" element={<ProductDetailPage />} />
          </Route>

          <Route path="services">
            <Route index element={<ServiceIndexPage />} />
            <Route path=":slug" element={<ServiceDetailPage />} />
          </Route>

          <Route path="industries">
            <Route index element={<IndustryIndexPage />} />
            <Route path=":slug" element={<IndustryDetailPage />} />
          </Route>

          <Route path="technology" element={<TechnologyPage />} />

          <Route path="case-studies">
            <Route index element={<CaseStudyIndexPage />} />
            <Route path=":slug" element={<CaseStudyDetailPage />} />
          </Route>

          <Route path="resources">
            <Route index element={<ResourceIndexPage basePath="/resources" />} />
            <Route path="blog">
              <Route index element={<ResourceIndexPage articleType="blog" basePath="/resources/blog" />} />
              <Route path=":slug" element={<ArticleDetailPage />} />
            </Route>
            <Route path="news">
              <Route index element={<ResourceIndexPage articleType="news" basePath="/resources/news" />} />
              <Route path=":slug" element={<ArticleDetailPage />} />
            </Route>
            <Route path="press-releases">
              <Route index element={<ResourceIndexPage articleType="press_release" basePath="/resources/press-releases" />} />
              <Route path=":slug" element={<ArticleDetailPage />} />
            </Route>
            <Route path="whitepapers">
              <Route index element={<ResourceIndexPage articleType="whitepaper" basePath="/resources/whitepapers" />} />
              <Route path=":slug" element={<ArticleDetailPage />} />
            </Route>
            <Route path="brochures">
              <Route index element={<ResourceIndexPage articleType="brochure" basePath="/resources/brochures" />} />
              <Route path=":slug" element={<ArticleDetailPage />} />
            </Route>
            <Route path="technical-articles">
              <Route
                index
                element={<ResourceIndexPage articleType="technical_article" basePath="/resources/technical-articles" />}
              />
              <Route path=":slug" element={<ArticleDetailPage />} />
            </Route>
            <Route path="downloads" element={<DownloadsPage />} />
          </Route>

          <Route path="careers">
            <Route index element={<CareersIndexPage />} />
            <Route path=":slug" element={<JobDetailPage />} />
          </Route>

          <Route path="about">
            <Route index element={<AboutPage />} />
            <Route path="leadership" element={<LeadershipPage />} />
            <Route path="vision-mission" element={<VisionMissionPage />} />
            <Route path="certifications" element={<CertificationsPage />} />
            <Route path="milestones" element={<MilestonesPage />} />
          </Route>

          <Route path="contact" element={<ContactPage />} />
          <Route path="sitemap" element={<SitemapPage />} />

          {/* Legal pages are CMS records rendered through one generic component. */}
          <Route path="privacy-policy" element={<CmsPage path="/privacy-policy" />} />
          <Route path="terms" element={<CmsPage path="/terms" />} />
          <Route path="cookie-policy" element={<CmsPage path="/cookie-policy" />} />

          {/* Legacy paths the backend also 301s, handled here for client-side navigation. */}
          <Route path="blog" element={<Navigate to="/resources/blog" replace />} />
          <Route path="news" element={<Navigate to="/resources/news" replace />} />
          <Route path="about-us" element={<Navigate to="/about" replace />} />
          <Route path="contact-us" element={<Navigate to="/contact" replace />} />
          <Route path="career" element={<Navigate to="/careers" replace />} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

export default App;
